import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, faqs } from '@/mocks/siteData';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }
    if (contactForm.message.length > 500) {
      setFormStatus('error');
      setFormError('留言内容不能超过500个字符');
      return;
    }
    setFormStatus('success');
    setContactForm({ name: '', email: '', message: '' });
    setTimeout(() => setFormStatus('idle'), 3000);
  };

  const milestones = [
    { year: '2018', title: '平台创立', desc: 'PG游戏平台正式成立，推出首批50款精品电子游戏' },
    { year: '2019', title: '快速发展', desc: '游戏数量突破200款，注册玩家超过100万' },
    { year: '2020', title: '技术创新', desc: '引入3D游戏引擎，推出沉浸式游戏体验' },
    { year: '2021', title: '全球拓展', desc: '支持多种语言和货币，服务全球玩家' },
    { year: '2022', title: '安全认证', desc: '通过国际权威安全认证，建立完善的风控体系' },
    { year: '2023', title: '生态完善', desc: '游戏数量突破1000款，活跃玩家超过500万' },
    { year: '2024', title: '行业领先', desc: '荣获多项行业大奖，成为全球顶级游戏平台' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Hero */}
        <section className="bg-background-100 py-12 md:py-18">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于{siteInfo.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.description}我们致力于为全球玩家提供最优质的游戏体验，以技术创新和用户至上的理念，打造值得信赖的游戏平台。
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  品牌介绍
                </h2>
                <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
                  <p>
                    PG游戏是全球领先的在线游戏平台，自2018年成立以来，始终秉承"品质为先、玩家至上"的核心理念。平台汇聚了超过<strong>1000款精品游戏</strong>，涵盖电子游戏、桌游、街机游戏、体育竞技、彩票游戏等多种品类，满足不同玩家的游戏需求。
                  </p>
                  <p>
                    我们拥有行业顶尖的技术团队，采用最新的游戏引擎和加密技术，为玩家提供<strong>流畅、安全、公平</strong>的游戏环境。所有游戏均经过严格的随机性测试和公平性认证，确保每位玩家都能享受公正的游戏体验。
                  </p>
                  <p>
                    PG游戏平台目前已服务超过<strong>500万活跃玩家</strong>，以99.9%的玩家好评率成为行业标杆。我们不断创新，持续推出新的游戏玩法和丰富的活动奖励，让每一次游戏都充满惊喜。
                  </p>
                </div>
              </div>
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20gaming%20platform%20headquarters%20interior%20with%20modern%20design%2C%20warm%20golden%20lighting%2C%20elegant%20office%20space%2C%20professional%20atmosphere%2C%20dark%20wood%20and%20golden%20accents%2C%20clean%20architectural%20photography&width=600&height=450&seq=about-brand-intro&orientation=landscape"
                  alt="PG游戏品牌介绍"
                  title="PG游戏品牌 - 关于我们"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8 md:mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden lg:block"></div>
              <div className="flex flex-col gap-6">
                {milestones.map((item, index) => (
                  <div key={index} className={`flex flex-col lg:flex-row items-start gap-4 lg:gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} hidden lg:block`}>
                      {index % 2 === 0 && (
                        <div className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                          <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base">{item.title}</h3>
                          <p className="text-xs md:text-sm text-foreground-600 mt-1">{item.desc}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 lg:gap-0 lg:justify-center w-full lg:w-auto">
                      <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-accent-500 text-white font-bold text-sm md:text-base z-10 flex-shrink-0">
                        {item.year.slice(2)}
                      </div>
                      <div className="lg:hidden flex-1 bg-background-50 rounded-lg border border-background-200/70 p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-accent-600 font-medium">{item.year}</span>
                          <h3 className="font-heading font-semibold text-foreground-900 text-sm">{item.title}</h3>
                        </div>
                        <p className="text-xs text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-left' : 'lg:text-right'} hidden lg:block`}>
                      {index % 2 === 1 && (
                        <div className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                          <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base">{item.title}</h3>
                          <p className="text-xs md:text-sm text-foreground-600 mt-1">{item.desc}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8 md:mb-10">
              平台口碑
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-user-star-line text-xl text-primary-600"></i>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground-900">500万+</p>
                <p className="text-sm text-foreground-500 mt-1">活跃玩家</p>
              </div>
              <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-gamepad-line text-xl text-accent-600"></i>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground-900">1000+</p>
                <p className="text-sm text-foreground-500 mt-1">精品游戏</p>
              </div>
              <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-shield-check-line text-xl text-secondary-600"></i>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground-900">99.9%</p>
                <p className="text-sm text-foreground-500 mt-1">好评率</p>
              </div>
              <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-global-line text-xl text-primary-600"></i>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-foreground-900">50+</p>
                <p className="text-sm text-foreground-500 mt-1">覆盖国家</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                  如有任何问题或建议，欢迎通过以下方式与我们联系。我们的客服团队将竭诚为您服务。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">客服热线</p>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  在线留言
                </h2>
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleContactSubmit}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6"
                >
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm((prev) => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        value={contactForm.message}
                        onChange={(e) => setContactForm((prev) => ({ ...prev, message: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors duration-200 resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1 text-right">{contactForm.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                    >
                      提交留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">留言已提交成功，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600 text-center">{formError || '提交失败，请重试'}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 text-center mb-8 md:mb-10">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {faqs.slice(0, 4).map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-800 list-none">
                    <span>{faq.question}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-14 md:py-20 bg-background-900 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20gaming%20energy%20background%20with%20flowing%20golden%20light%20particles%2C%20dark%20elegant%20atmosphere%2C%20warm%20amber%20accents%2C%20dynamic%20motion%20trails%2C%20cinematic%20composition&width=1600&height=600&seq=about-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
          />
          <div className="relative z-10 text-center px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-white mb-3">
              准备好开始你的游戏之旅了吗？
            </h2>
            <p className="text-sm md:text-base text-white/70 mb-6 max-w-lg mx-auto">
              加入500万玩家的行列，探索PG游戏的精彩世界
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="#" className="w-full sm:w-auto px-8 py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">
                立即注册
              </a>
              <Link to="/category/dianzi-youxi" className="w-full sm:w-auto px-8 py-3 rounded-lg border border-white/30 text-white font-medium text-sm hover:bg-white/10 transition-colors duration-200 whitespace-nowrap">
                浏览游戏
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于我们 - ${siteInfo.name}`,
            description: `${siteInfo.name}品牌介绍与联系方式`,
            url: `${siteInfo.url}/about`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: siteInfo.url,
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
    </>
  );
}